import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { storeHref } from './marketplaceFilters';

export interface MarketplaceCrumb {
    label: string;
    /** Without a target the crumb is plain text (a group, not a page); the last crumb is the current page. */
    to?: string;
}

/**
 * Top bar of every subpage of the marketplace: the way back to the front page and the
 * breadcrumb on one line (they wrap below each other only when the line is too narrow).
 */
export default function MarketplaceNav({ crumbs }: { crumbs: MarketplaceCrumb[] }) {
    const { t } = useTranslation();
    return (
        <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-2">
            <Link
                to={storeHref()}
                className="inline-flex shrink-0 items-center gap-2 rounded-sm text-sm font-semibold text-foreground transition-colors hover:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
                <ArrowLeft aria-hidden className="h-4 w-4" />
                {t('agentPanel.store.offer.backToMarketplace')}
            </Link>
            <span aria-hidden className="hidden h-4 w-px bg-border sm:block" />
            <Breadcrumb aria-label={t('agentPanel.store.offer.breadcrumb')} className="min-w-0">
                <BreadcrumbList>
                    {crumbs.map((crumb, index) => {
                        const last = index === crumbs.length - 1;
                        return (
                            <Fragment key={`${index}-${crumb.label}`}>
                                {index > 0 && <BreadcrumbSeparator />}
                                <BreadcrumbItem className="min-w-0">
                                    {last ? (
                                        <BreadcrumbPage className="truncate [&[role=link]]:!text-foreground">{crumb.label}</BreadcrumbPage>
                                    ) : crumb.to ? (
                                        <Link className="text-muted-foreground transition-colors hover:text-foreground" to={crumb.to}>
                                            {crumb.label}
                                        </Link>
                                    ) : (
                                        <span>{crumb.label}</span>
                                    )}
                                </BreadcrumbItem>
                            </Fragment>
                        );
                    })}
                </BreadcrumbList>
            </Breadcrumb>
        </div>
    );
}
